import { useItemsForLabelList } from '@/core/domain/stock/itemsLabel/items-label.hooks';
import {
  ItemLabel,
  ItemLabelFilterType,
  ItemLabelOrder,
  ItemLabelOrderColumnsLabel,
  ItemLabelRequest,
} from '@/core/domain/stock/itemsLabel/items-label.types';
import { Alert, Checkbox, Modal } from 'antd';
import InfiniteTable, { ColumnsType } from '../InfiniteTable';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { useCallback, useEffect, useState } from 'react';
import { SorterResult } from 'antd/lib/table/interface';
import AppPageHeader from '../AppPageHeader';
import Group from '../Group';
import ItemsForLabelExtraFilter from './ExtraFilter';
import { useDebounce } from '@/hooks';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  companyId?: number;
  partsSelected?: ItemLabel[];
  onPartsSelect(parts: ItemLabel[]): void;
  allPartsSelected?: boolean;
  onAllPartsSelected(value: boolean): void;
  onFilterParts(value: ItemLabelRequest): void;
}

export default function ItemsForLabelModalSelect(props: Props) {
  const [allPartsSelected, setAllPartsSelected] = useState(false);
  const [partsSelected, setPartsSelected] = useState<ItemLabel[]>([]);

  const [queryParams, setQueryParams] = useState<ItemLabelRequest>({
    filterType: ItemLabelFilterType.Free,
  });

  useEffect(() => {
    if (props.opened) {
      setAllPartsSelected((prev) =>
        typeof props.allPartsSelected === 'boolean'
          ? props.allPartsSelected
          : prev,
      );
    }
  }, [props.opened, props.allPartsSelected]);

  useEffect(() => {
    if (props.opened) {
      setPartsSelected(props.partsSelected || []);
    }
  }, [props.opened, props.partsSelected]);

  const columnPartCode = useConfigurationColumnsPart({
    isSku: true,
    columnsKey: {
      barcode: ItemLabelOrderColumnsLabel.Barcode,
      companyCode: ItemLabelOrderColumnsLabel.CompanyCode,
      manufacturingCode: ItemLabelOrderColumnsLabel.ManufacturingCode,
      sku: ItemLabelOrderColumnsLabel.Sku,
    },
  });

  const queryDebounce = useDebounce(queryParams);

  const itemsForLabelList = useItemsForLabelList(
    {
      ...queryDebounce,
      page: 1,
      companyId: props.companyId,
    },
    props.opened,
  );

  const parts = itemsForLabelList.data?.pages.flatMap(
    (page) => page.result?.data || [],
  );

  const handleClose = () => {
    setAllPartsSelected(false);
    setPartsSelected([]);
    props.onClose();
    itemsForLabelList.remove();
  };

  const columns: ColumnsType<ItemLabel> = [
    columnPartCode,
    {
      key: ItemLabelOrderColumnsLabel.ManufacturingCode,
      ellipsis: true,
      dataIndex: ['manufacturer', 'name'],
      title: 'Fabricante',
      width: 200,
      sorter: true,
    },
    {
      key: ItemLabelOrderColumnsLabel.Description,
      ellipsis: true,
      dataIndex: 'description',
      title: 'Descrição',
      width: 300,
      sorter: true,
    },
  ];

  function handleSorter(sorter: SorterResult<ItemLabel>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order =
            sorter.order === 'ascend'
              ? 0
              : sorter.order === 'descend'
              ? 1
              : undefined;

          if (prev.orderColumns === sorter.columnKey) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: ItemLabelOrder.ASC,
            orderColumns: sorter.columnKey as ItemLabelOrderColumnsLabel,
          };
        }

        return prev;
      });
    }
  }

  const handleOk = useCallback(() => {
    if (allPartsSelected) {
      props.onPartsSelect([]);
      props.onFilterParts(queryParams);
    } else {
      props.onPartsSelect(partsSelected);
    }
    props.onAllPartsSelected(allPartsSelected);
    handleClose();
  }, [partsSelected, allPartsSelected, queryParams]);

  const isSelectionUnchanged =
    !partsSelected?.length ||
    (partsSelected?.length === props.partsSelected?.length &&
      partsSelected?.every((part) => props.partsSelected?.includes(part)));

  return (
    <Modal
      width={800}
      open={props.opened}
      title="Seleção de peças"
      onOk={handleOk}
      okText={
        allPartsSelected ? 'Selecionar todas as peças' : 'Selecionar peças'
      }
      okButtonProps={{ disabled: isSelectionUnchanged && !allPartsSelected }}
      onCancel={handleClose}
      confirmLoading={itemsForLabelList.isLoading}
      cancelButtonProps={{ disabled: itemsForLabelList.isLoading }}
      closable={!itemsForLabelList.isLoading}
      style={{ position: 'relative' }}
    >
      <AppPageHeader
        searchValue={queryParams.search}
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        extraFilters={
          <ItemsForLabelExtraFilter
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            inModal
          />
        }
      />

      <Group position="between" style={{ marginBottom: 24 }}>
        <div>
          {!!partsSelected.length && !allPartsSelected && (
            <strong style={{ color: 'orange' }}>
              Clique no <i>checkbox</i> para remover a seleção
            </strong>
          )}
        </div>

        <Checkbox
          checked={allPartsSelected}
          onChange={({ target }) => {
            setAllPartsSelected(target.checked);
            setPartsSelected([]);
          }}
        >
          Todas as peças
        </Checkbox>
      </Group>

      {allPartsSelected && (
        <Alert
          type="warning"
          description="Os campos de filtros influênciam na criação das etiquetas."
          style={{ marginBottom: 24 }}
        />
      )}

      <InfiniteTable
        columns={columns}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleSorter(sorter);
          }
        }}
        hasNextPage={itemsForLabelList.hasNextPage}
        fetchNextPage={itemsForLabelList.fetchNextPage}
        dataSource={parts}
        loading={itemsForLabelList.isFetching}
        rowKey={(row) => row.partId}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: allPartsSelected
            ? []
            : partsSelected?.map((part) => part?.partId),
          getCheckboxProps: () => ({
            disabled: allPartsSelected,
          }),
          onChange: (selectedRowKeys, selectedRows) => {
            setPartsSelected((prevPartsSelected) => {
              const uniqueItems = selectedRows.filter(
                (newItem) =>
                  !!newItem &&
                  !prevPartsSelected.some(
                    (existingItem) => existingItem.partId === newItem.partId,
                  ),
              );

              return [
                ...prevPartsSelected.filter((item) =>
                  selectedRowKeys.includes(item.partId),
                ),
                ...uniqueItems,
              ];
            });
          },
        }}
      />
    </Modal>
  );
}
